import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useConfirm, useToast } from "../../components/ui";
import { api } from "../../lib/api";

interface SupportAccess {
  grant: { expiresAt: string; createdAt: string; grantedBy: string | null } | null;
  visits: { who: string; what: string | null; at: string }[];
}

const HOURS = [
  { hours: 24, label: "24 horas" },
  { hours: 72, label: "3 días" },
  { hours: 168, label: "7 días" },
] as const;

/** What a support look was, in the owner's words. */
const WHAT: Record<string, string> = {
  conversations: "la lista de conversaciones",
  conversation: "una conversación",
  contacts: "la lista de contactos",
  contact: "un contacto",
  cases: "la lista de casos",
  case: "un caso",
};

const when = new Intl.DateTimeFormat("es-CR", {
  day: "numeric",
  month: "short",
  hour: "numeric",
  minute: "2-digit",
  timeZone: "America/Costa_Rica",
});

/**
 * Orbis's support never sees the company's CRM on its own (f6-15). The owner opens it
 * for a while when they ask for help, can close it any time, and sees every look.
 */
export function SupportAccessCard() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [hours, setHours] = useState<number>(24);
  const access = useQuery({
    queryKey: ["support-access"],
    queryFn: () => api.get<SupportAccess>("/support-access"),
    // Changes arrive live; this only catches the moment the time runs out.
    refetchInterval: 60_000,
  });
  const done = () => qc.invalidateQueries({ queryKey: ["support-access"] });
  const open = useMutation({
    mutationFn: (h: number) => api.post("/support-access", { hours: h }),
    onSuccess: () => {
      done();
      toast("Acceso al soporte activado");
    },
    onError: () => toast("No se pudo activar. Intenta de nuevo."),
  });
  const close = useMutation({
    mutationFn: () => api.delete("/support-access"),
    onSuccess: () => {
      done();
      toast("Acceso al soporte quitado");
    },
    onError: () => toast("No se pudo quitar. Intenta de nuevo."),
  });
  const grant = access.data?.grant ?? null;
  const visits = access.data?.visits ?? [];

  return (
    <div className="card">
      <div className="card-body support-access">
        <div>
          <h3>Acceso del soporte de Orbis</h3>
          <p className="muted">
            Sin tu permiso, el equipo de Orbis no ve tus conversaciones, contactos ni casos. Si nos pides ayuda, dale
            acceso por un tiempo: solo puede mirar, no cambia nada, y se apaga solo.
          </p>
        </div>
        {grant ? (
          <div className="support-access-row">
            <p>
              <span className="pill pill-human">Activo</span> hasta el {when.format(new Date(grant.expiresAt))}
              {grant.grantedBy ? `, lo activó ${grant.grantedBy}` : ""}.
            </p>
            <button
              type="button"
              className="btn btn-sm"
              disabled={close.isPending}
              onClick={async () => {
                const ok = await confirm({
                  title: "¿Quitar el acceso del soporte?",
                  message: "El equipo de Orbis deja de ver tu CRM ahora mismo.",
                  confirmLabel: "Quitar acceso",
                });
                if (ok) close.mutate();
              }}
            >
              Quitar acceso
            </button>
          </div>
        ) : (
          <div className="support-access-row">
            <label className="field">
              <span>Por cuánto tiempo</span>
              <select className="select" value={hours} onChange={(e) => setHours(Number(e.target.value))}>
                {HOURS.map((h) => (
                  <option key={h.hours} value={h.hours}>
                    {h.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn btn-primary"
              disabled={open.isPending || access.isPending}
              onClick={() => open.mutate(hours)}
            >
              Dar acceso
            </button>
          </div>
        )}
        {visits.length > 0 && (
          <details className="support-visits">
            <summary>Lo que miró el soporte ({visits.length})</summary>
            <ul>
              {visits.map((v) => (
                <li key={`${v.at}-${v.what}-${v.who}`}>
                  <span>
                    {v.who} miró {(v.what && WHAT[v.what]) ?? "tu CRM"}
                  </span>
                  <span className="faint">{when.format(new Date(v.at))}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}
